/**
 * "Sort report" — the two-level order of the Dining & Events Bookings report.
 *
 * A PRIMARY key groups the bookings; a SECONDARY key orders them inside each
 * group. The default — Date, then Carriage — puts every booking under its
 * date and, within the date, lines the carriages up in order. The same order
 * feeds the on-screen grid, the Excel export and the printed carriage report,
 * whose headings ARE these two keys (see bookingsCarriagePdf.ts).
 *
 * One owner: `compareRows(spec)` is the only sort the Bookings report applies.
 */
import { cmpValues, prettyDateLong, timeLabel, type Row } from "./bookingsShared";

export type SortKeyId = "date" | "time" | "carriage" | "seats" | "account";

export interface SortOption {
  id: SortKeyId;
  /** Label in the dropdowns. */
  label: string;
  /** The row field the key sorts and groups on. */
  key: string;
  /** Every group of this key starts a fresh page in the printed report. */
  freshPage: boolean;
  /** How a group heading for this key reads on the printed report. */
  heading: (row: Row) => string;
}

function text(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

export const SORT_OPTIONS: readonly SortOption[] = [
  { id: "date", label: "Date", key: "JourneyDate", freshPage: true, heading: (r) => prettyDateLong(r.JourneyDate) || "No date" },
  { id: "time", label: "Time", key: "Time", freshPage: true, heading: (r) => (timeLabel(r.Time) ? `Departing ${timeLabel(r.Time)}` : "No time") },
  { id: "carriage", label: "Carriage", key: "Carriage", freshPage: true, heading: (r) => text(r.Carriage) || "No carriage" },
  { id: "seats", label: "Seats", key: "Seats", freshPage: false, heading: (r) => (text(r.Seats) ? `Seats ${text(r.Seats)}` : "No seats") },
  { id: "account", label: "Booking Account", key: "AccName", freshPage: false, heading: (r) => text(r.AccName) || "No account name" },
];

export interface SortSpec {
  primary: SortKeyId;
  secondary: SortKeyId;
}

export const DEFAULT_SORT: SortSpec = { primary: "date", secondary: "carriage" };

export function sortOption(id: SortKeyId): SortOption {
  return SORT_OPTIONS.find((o) => o.id === id) ?? SORT_OPTIONS[0];
}

/** The value a row has for a sort key, as the report groups it (trimmed text). */
export function groupValue(opt: SortOption, row: Row): string {
  return text(row[opt.key]);
}

/** "Date, then Carriage" — for the report's subtitle. */
export function describeSort(spec: SortSpec): string {
  return `${sortOption(spec.primary).label}, then ${sortOption(spec.secondary).label}`;
}

function trimmed(v: unknown): unknown {
  return typeof v === "string" ? v.trim() : v;
}

/**
 * Ascending comparator: the primary key, then the secondary, then every other
 * key in the fixed order date → time → carriage → seats → account, then the
 * booking reference and the item — so rows equal on the chosen keys always
 * land in the same, sensible order.
 */
export function compareRows(spec: SortSpec): (a: Row, b: Row) => number {
  const chain: string[] = [];
  const push = (k: string) => {
    if (!chain.includes(k)) chain.push(k);
  };
  push(sortOption(spec.primary).key);
  push(sortOption(spec.secondary).key);
  for (const o of SORT_OPTIONS) push(o.key);
  push("Booking");
  push("Item");
  return (a, b) => {
    for (const k of chain) {
      const c = cmpValues(trimmed(a[k]), trimmed(b[k]));
      if (c !== 0) return c;
    }
    return 0;
  };
}

/**
 * The pair of dropdowns. Choosing, for one slot, the key the other slot holds
 * swaps them — the two keys are always different, and nothing is ever
 * silently ignored.
 */
export function SortReportControls({
  value,
  onChange,
}: {
  value: SortSpec;
  onChange: (v: SortSpec) => void;
}) {
  const set = (slot: keyof SortSpec, id: SortKeyId) => {
    const other: keyof SortSpec = slot === "primary" ? "secondary" : "primary";
    if (value[other] === id) onChange({ primary: value.secondary, secondary: value.primary });
    else onChange({ ...value, [slot]: id });
  };
  return (
    <div className="sort-report" role="group" aria-label="Sort report">
      {(["primary", "secondary"] as const).map((slot) => (
        <label key={slot} className="sort-report__field">
          <span>{slot === "primary" ? "Primary sort" : "Secondary sort"}</span>
          <select
            value={value[slot]}
            onChange={(e) => set(slot, e.target.value as SortKeyId)}
            title={slot === "primary" ? "Group the report by this" : "Order the bookings inside each group by this"}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
}
